import { OutlineLocationIcon } from "@App/Components/common/Icons";
import { Button } from "@App/Components/Funnel/Button";
import { type FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "react-calendar/dist/Calendar.css";
import { Headline } from "@App/Components/atoms/common/Headline";
import { Checkbox } from "@App/Components/common/Checkbox";
import { DesiredDatePicker } from "@App/Components/molecules/common/datePicker/DesiredDatePicker";
import { useReturnDetails } from "@App/hooks/vehicleReturn/useReturnDetails";
import { AddressModal } from "@App/Pages/VehicleReturn/AddressModal";
import { selectIsAuthenticated } from "@App/state/selectors/vehicleReturn";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export type DeliveryDetailsProps = {
  onSubmit: () => void;
};

export const ReturnDetails: FunctionComponent<DeliveryDetailsProps> = ({
  onSubmit,
}: DeliveryDetailsProps) => {
  const { t } = useTranslation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) navigate("../", { replace: true });
  }, [isAuthenticated, navigate]);

  const {
    pickUpAddress,
    changeAddress,
    openAddressModal,
    closeAddressModal,
    showAddressModal,
    dates: { minDate, maxDate, selectedDates, isDateDisabled, selectedDatesSetters },
    costAccept,
    setCostAccept,
    isButtonDisabled,
    submit,
    isContactDataComplete,
    isEmailValid,
  } = useReturnDetails(onSubmit);

  return (
    <div className={"container"}>
      <div className="container md:w-[738px] md:min-h-[calc(100vh-200px)] items-start my-10 mx-auto">
        <div className={"bg-gray-50 p-4 rounded-md flex flex-col gap-1 mb-5 w-fit shadow-sm"}>
          <p className={"text-sm text-gray-800"}>
            Sie möchten Ihr Fahrzeug doch lieber bei einem TÜV-Standort abgeben?
          </p>
          <button
            type={"button"}
            onClick={() => navigate("../../")}
            className={"text-left font-bold underline text-sm"}
          >
            Zur Fahrzeugrückgabe
          </button>
        </div>
        <Headline type={"h1"}>{t("vehicleReturn.details-headline")}</Headline>
        <div className="w-full md:w-1/2 flex shadow rounded-lg p-5 mb-10">
          <OutlineLocationIcon className={"w-5 h-w mr-3"} color={"primary-900"} />
          <div>
            <div className={"font-bold"}>{t("vehicleReturn.details-address-headline")}</div>
            <div>{pickUpAddress.name}</div>
            <div>{pickUpAddress.phone}</div>
            <div>{pickUpAddress.street}</div>
            <div>
              {pickUpAddress.postcode} {pickUpAddress.city}
            </div>
            <div>{pickUpAddress.email}</div>
            {!isContactDataComplete && (
              <p className={"text-red-500 mt-3"}>
                Bitte überprüfen Sie Ihre Daten auf Vollständigkeit
              </p>
            )}
            {isContactDataComplete && !isEmailValid && (
              <p className={"text-red-500 mt-3"}>{t("vehicleReturn.details-email-invalid")}</p>
            )}
            <button
              type={"button"}
              className={"text-primary cursor-pointer mt-3"}
              onClick={openAddressModal}
            >
              Ändern
            </button>
          </div>
        </div>
        <h2 className={"text-xl font-serif mb-3"}>{t("vehicleReturn.details-date-headline")}</h2>
        <p className={"text-neutral-500 mb-5"}>{t("vehicleReturn.details-date-text")}</p>

        <div className={"flex flex-col md:flex-row justify-between gap-3 mt-3"}>
          <DesiredDatePicker
            id="1"
            label={t("vehicleReturn.details-date-1")}
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDates.date1}
            onChange={selectedDatesSetters.date1}
            dateDisabled={({ date }) => isDateDisabled(date)}
          />
          <DesiredDatePicker
            id="2"
            label={t("vehicleReturn.details-date-2")}
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDates.date2}
            onChange={selectedDatesSetters.date2}
            dateDisabled={({ date }) => isDateDisabled(date)}
          />
          <DesiredDatePicker
            id="3"
            label={t("vehicleReturn.details-date-3")}
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDates.date3}
            onChange={selectedDatesSetters.date3}
            dateDisabled={({ date }) => isDateDisabled(date)}
          />
        </div>
        <div className="flex mt-9 font-weight-bold">
          <Checkbox id={"costAccept"} status={costAccept} onChange={setCostAccept} />
          <label htmlFor={"costAccept"}>
            {t("vehicleReturn.details-cost-accept")}&nbsp;
            <a
              href={
                "https://werkstatt.repareo.de/storage/downloads?file=%2Fhome%2Fpartners%2Fsantander%2FLieferkosten_Spedition_Santander_Stand_122025.pdf&disk=repareoFilesharing"
              }
              target={"_blank"}
              rel={"noreferrer"}
            >
              Informationen zu Speditionskosten bei Haustürabholung
            </a>
          </label>
        </div>
        <Button
          title={t("vehicleReturn.details-button")}
          onClick={submit}
          disabled={isButtonDisabled}
        />

        {showAddressModal && (
          <AddressModal
            customer={{
              name: pickUpAddress.name ?? "",
              phone: pickUpAddress.phone ?? "",
              street: pickUpAddress.street ?? "",
              city: pickUpAddress.city ?? "",
              postcode: pickUpAddress.postcode ?? "",
              email: pickUpAddress.email ?? "",
            }}
            closeModal={closeAddressModal}
            submit={changeAddress}
          />
        )}
      </div>
    </div>
  );
};
